import { useSyncExternalStore } from 'react';

export const MENU_MULTI_OPEN_STORAGE_KEY = 'Layout.menuMultiOpen';
const CHANGE_EVENT = 'menu-multi-open-changed';

/**
 * When on, opening a menu section no longer closes the previous one, so several can stay expanded.
 * Off by default — the accordion keeps exactly one section open, which is the designed behaviour.
 *
 * Kept in its own store rather than useLocalStorage because the switch lives on the profile page
 * while the reader is the sidebar, which stays mounted across navigation: useLocalStorage does not
 * sync between components, so the menu would not react until a reload.
 */
export const readMenuMultiOpen = (): boolean => {
  try {
    return localStorage.getItem(MENU_MULTI_OPEN_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const writeMenuMultiOpen = (v: boolean) => {
  try {
    localStorage.setItem(MENU_MULTI_OPEN_STORAGE_KEY, v ? '1' : '0');
  } catch {
    // ignore
  }
  // localStorage writes don't fire 'storage' in the same tab — broadcast manually.
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

export const subscribeMenuMultiOpen = (cb: () => void): (() => void) => {
  const onStorage = (e: StorageEvent) => {
    if (e.key === MENU_MULTI_OPEN_STORAGE_KEY) cb();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CHANGE_EVENT, cb);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CHANGE_EVENT, cb);
  };
};

export const useMenuMultiOpen = (): boolean =>
  useSyncExternalStore(subscribeMenuMultiOpen, readMenuMultiOpen, () => false);
